import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogActions,
  Stack,
  Button,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import CommonGrid from "../../../components/Repository/CommonGrid";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../../../redux/api";
import { getTemplatePath } from "./ServerRevisionHistory";
import { cloneDeep } from "lodash";
import axios from "axios";
import {
  hasValidRevisionTemplatePayload,
  persistRetrievedRevisionToUserSpace,
} from "../../../utils/persistUserSpaceTemplateFromRevision";

function CustomIconRenderer(params) {
  return (
    <>
      <div style={{ display: "flex" }}>
        <span className={"REVISION_HISTORY"} style={{ margin: "5px" }}></span>
        <p style={{ margin: "0px 5px" }}>{params.value}</p>
      </div>
    </>
  );
}

function LocalRevisionHistory({
  open,
  onClose,
  node,
  onRefreshTree,
  urlParams,
}) {
  const { t } = useTranslation();
  const [rowData, setRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [commentModal, setCommentModal] = useState({
    isOpen: false,
    data: {},
  });
  const [path, setPath] = useState("");
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    message: "",
    type: "info",
  });
  const [compareModal, setCompareModal] = useState(false);
  const [htmlContent, setHtmlContent] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const dispatch = useDispatch();
  const { account, user, baseUrl, token } = useSelector(
    (state) => state.queryParams
  );
  const treeData = useSelector((state) => state.treeData);
  const currentScreen = useSelector((state) => state.treeData.currentScreen);

  const onCommentClose = () =>
    setCommentModal({ ...commentModal, isOpen: false, data: {} });

  const onAlertClose = () =>
    setAlertModal({ isOpen: false, message: "", type: "info" });

  const compareModalHandler = () => {
    setCompareModal(!compareModal);
    if (!compareModal) {
      setHtmlContent(null);
    }
  };

  const fetchDiffContent = async (firstRev, secondRev) => {
    setDiffLoading(true);
    try {
      const url = `${baseUrl}/workbench?user=${user}&action=get_server_revision_diff&account=${account}&path=${path}&rev_id1=${firstRev.local_revision_id}&rev_id2=${secondRev.local_revision_id}&ref_id1=${firstRev.server_revision_id}&userspace=true&ref_id2=${secondRev.server_revision_id}`;

      const response = await axios(url, {
        responseType: "text",
        headers: { Authorization: `Bearer ${token}` },
      });

      setHtmlContent(response.data);
    } catch (error) {
      console.error("Error fetching diff:", error);
      setHtmlContent(error.response?.data || "Error fetching diff data");
      setAlertModal({
        isOpen: true,
        message: t("COMPARE_ERROR"),
        type: "error",
      });
    } finally {
      setDiffLoading(false);
    }
  };

  const retrieveHandler = () => {
    if (selectedRows.length === 0) {
      setAlertModal({
        isOpen: true,
        message: t("BOX_RETRIEVE_ALERT"),
        type: "warning",
      });
      return;
    }
    const revisionArray = cloneDeep(selectedRows);

    revisionArray.sort((a, b) => {
      const compareFactor1 = +(
        a.server_revision_id +
        "." +
        a.local_revision_id
      );
      const compareFactor2 = +(
        b.server_revision_id +
        "." +
        b.local_revision_id
      );
      return compareFactor1 > compareFactor2 ? 1 : -1;
    });

    const rowToRetrieve = revisionArray[revisionArray?.length - 1];
    if (revisionArray?.length > 0) {
      dispatch(
        api.endpoints.get_user_template_by_revision_history_id.initiate({
          account,
          path,
          revid: rowToRetrieve.server_revision_id,
          user_revid: rowToRetrieve.local_revision_id,
        })
      )
        .then((res) => {
          if (res.error) {
            throw new Error("fetch");
          }
          const templateRecord = res.data;
          if (!hasValidRevisionTemplatePayload(templateRecord)) {
            throw new Error("shape");
          }
          return persistRetrievedRevisionToUserSpace(dispatch, {
            account,
            workflowGroupName: node?.workflowGroup,
            templateName: node?.name || node?.title,
            templatePath: path,
            templateApiRecord: templateRecord,
          });
        })
        .then(() => {
          setAlertModal({
            isOpen: true,
            message: t("RETRIEVE_SUCCESS"),
            type: "success",
          });
          if (onRefreshTree) {
            onRefreshTree();
          }
          onClose();
        })
        .catch((error) => {
          console.error("Retrieve or persist failed:", error);
          setAlertModal({
            isOpen: true,
            message: t(
              error?.message === "shape" ? "RETRIEVE_FAILED" : "RETRIEVE_ERROR"
            ),
            type: "error",
          });
          onClose();
        });
    }
  };

  const compareHandler = () => {
    if (selectedRows.length === 2) {
      const revisionArray = cloneDeep(selectedRows);
      revisionArray.sort((a, b) => {
        const compareFactor1 = +(
          a.server_revision_id +
          "." +
          a.local_revision_id
        );
        const compareFactor2 = +(
          b.server_revision_id +
          "." +
          b.local_revision_id
        );
        return compareFactor1 > compareFactor2 ? 1 : -1;
      });

      setSelectedRows(revisionArray);
      fetchDiffContent(revisionArray[0], revisionArray[1]);
      compareModalHandler();
    } else {
      setAlertModal({
        isOpen: true,
        message: t("BOX_SEL_REV"),
        type: "warning",
      });
    }
  };

  const getGridAPI = (api) => {
    setGridApi(api);
  };

  function onSelectionChanged() {
    const rows = gridApi.getSelectedNodes();

    if (rows.length === 0) setSelectedRows([]);

    if (rows.length > 0) {
      const rowData = rows?.slice(0, 2).map((row) => {
        return row.data;
      });
      setSelectedRows(rowData);

      rows.slice(2, rows.length).forEach((row) => {
        row.setSelected(false);
      });
    }
  }

  const onCellDoubleClicked = (params) => {
    setCommentModal((prevState) => ({
      isOpen: true,
      data: params.data,
    }));
  };

  const gridOptions = {
    columnDefs: [
      {
        headerName: t("COL_SVR_REV_ID"),
        field: "server_revision_id",
        showPlaceholder: false,
        resizable: true,
        cellRenderer: CustomIconRenderer,
        flex: 1,
      },
      {
        headerName: t("COL_LOCAL_REV_ID"),
        field: "local_revision_id",
        showPlaceholder: false,
        resizable: true,
        flex: 1,
      },
      {
        headerName: t("DATE_"),
        field: "date",
        showPlaceholder: false,
        resizable: true,
        flex: 1,
      },
      {
        headerName: t("LBL_NEW_COMMENT"),
        field: "comment",
        showPlaceholder: false,
        resizable: true,
        flex: 2,
      },
    ],
  };
  useEffect(() => {
    if (open) {
      const path = getTemplatePath(
        account,
        node.workflowGroup,
        node.type,
        node.title
      );
      dispatch(
        api.endpoints.get_user_revision_history.initiate({
          account,
          path,
        })
      )
        .then((response) => {
          const data = response.data;
          const updatedRowData = data.map((row, index) => {
            return {
              server_revision_id:
                row.revisionId === "null" ? "0" : row.revisionId,
              local_revision_id: row.localRevisionId,
              date: row.lastModifiedDate,
              comment: row.comment,
            };
          });
          setRowData(updatedRowData);
        })
        .catch((error) => {
          setRowData([error]);
        })
        .finally(() => {
          setIsLoading(false);
        });
      setPath(path);
    }
  }, [open]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle className="dialog-header" id="alert-dialog-title">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div className="title">{t("LBL_LOCAL_USER_REVISION_HISTORY")}</div>
            <IconButton onClick={onClose} sx={{ borderRadius: "5px" }}>
              <CloseIcon
                sx={{ height: "28px", width: "28px", color: "#777777" }}
              />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent sx={{ height: "100%" }} dividers>
          <div className={"dialog-content"} style={{ overflow: "hidden" }}>
            <span>{t("DIAG_REVISION_HISTORY_DESC")}</span>
          </div>
          <div style={{ height: "100%" }}>
            <CommonGrid
              rowData={rowData}
              gridOptions={gridOptions}
              getGridAPI={getGridAPI}
              isLoading={isLoading}
              containerClasses={"server-rev-grid-container"}
              gridProps={{
                animateRows: false,
                enableCellTextSelection: true,
                ensureDomOrder: true,
                rowSelection: "multiple",
                onSelectionChanged: onSelectionChanged,
                onCellDoubleClicked: onCellDoubleClicked,
              }}
              fullSize={true}
            />
          </div>
          <div className={"diag-lrh-ib_footer"} style={{ marginTop: ".5rem" }}>
            <span>{t("DIAG_COMMENT_HINT")}</span>
          </div>
        </DialogContent>
        <DialogActions sx={{ my: 1 }}>
          <Stack direction={"row"} spacing={"8px"}>
            <Tooltip
              title={
                selectedRows.length < 2 ? t("SELECT_TWO_ROWS_TO_COMPARE") : ""
              }>
              <span>
                <Button
                  size="small"
                  variant={"outlined"}
                  onClick={compareHandler}
                  disabled={selectedRows.length < 2}>
                  {t("BTN_COMPARE")}
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                selectedRows.length === 0
                  ? t("SELECT_AT_LEAST_ONE_ROW_TO_RETRIEVE")
                  : ""
              }>
              <span>
                <Button
                  size="small"
                  variant={"outlined"}
                  onClick={retrieveHandler}
                  disabled={selectedRows.length === 0}>
                  {t("BTN_RETRIEVE")}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </DialogActions>
      </Dialog>
      {commentModal.isOpen && (
        <Dialog
          open={commentModal.isOpen}
          maxWidth={"xs"}
          fullWidth
          onClose={onCommentClose}>
          <DialogTitle>
            <div className="diag-lrh-ib">
              {t("REVISION")} {commentModal.data.revisionId}
            </div>
          </DialogTitle>
          <DialogContent dividers>
            <div
              className={"diag-lrh-ib_header"}
              style={{ marginTop: ".5rem", marginBottom: "1rem" }}>
              <span>
                {t("REVISION")}: {commentModal.data.server_revision_id}
              </span>
              <span>
                {t("LOCAL_REVISION")}: {commentModal.data.local_revision_id}
              </span>
              <span>
                {t("DATE_")}: {commentModal.data.date}
              </span>
              <span>
                {t("COMMENT")}: {commentModal.data.comment}
              </span>
            </div>
          </DialogContent>
          <DialogActions>
            <Button size="small" variant={"outlined"} onClick={onCommentClose}>
              {t("BTN_OK")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {compareModal && (
        <Dialog
          open={compareModal}
          onClose={compareModalHandler}
          aria-labelledby="diff-dialog-title"
          aria-describedby="diff-dialog-description"
          maxWidth={"xl"}
          fullWidth>
          <DialogTitle>
            <div className="compare-local-revision_header dialog-header">
              <h1>{path}</h1>
            </div>
          </DialogTitle>
          <DialogContent
            className="compare-local-revisions_body height-100"
            dividers>
            <div id="local-revision-diff-container" style={{ height: "100%" }}>
              {htmlContent ? (
                <iframe
                  srcDoc={htmlContent}
                  style={{ width: "100%", height: "100%", border: "none" }}
                  title="HTML Content Viewer"></iframe>
              ) : diffLoading ? (
                <div
                  className="loading-overlay"
                  style={{ marginTop: "15%", display: "grid", gap: "20px" }}>
                  <CircularProgress size={"4rem"} />
                  <div>{t("LOADING_IND")}</div>
                </div>
              ) : (
                ""
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              size="small"
              onClick={compareModalHandler}
              variant={"outlined"}>
              {t("BTN_CLOSE")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {alertModal.isOpen && (
        <Snackbar
          open={alertModal.isOpen}
          autoHideDuration={6000}
          onClose={onAlertClose}>
          <Alert
            severity={alertModal.type}
            onClose={onAlertClose}
            variant="filled"
            sx={{
              width: "100%",
              maxWidth: "450px",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}>
            {alertModal.message}
          </Alert>
        </Snackbar>
      )}
    </>
  );
}

export default LocalRevisionHistory;
